import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { AlertCircleIcon, EyeIcon, EyeOffIcon } from 'lucide-react-native';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { z } from 'zod';

import { Header } from '@/components/header';
import { Heading } from '@/components/heading';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Input, InputField, InputIcon, InputSlot } from '@/components/ui/input';
import { Link, LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import UnauthenticatedLayout from '@/components/unauthenticatedLayout';
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from '@/constants/links';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import { useColors } from '@/hooks/useColors';
import { useRedirectPath } from '@/hooks/useRedirectPath';
import { useToaster } from '@/hooks/useToaster';
import { COGNITO_EXCEPTIONS } from '@/services/auth';
import { trackPasswordChangeFailed, trackPasswordChanged } from '@/util/analyticsEvents';

const paramsSchema = z.object({
  email: z.string().email(),
  verificationCode: z.string().min(1),
});

const formSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must include an uppercase letter')
      .regex(/[a-z]/, 'Password must include a lowercase letter')
      .regex(/[0-9]/, 'Password must include a number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

// https://developer.apple.com/password-rules/
const passwordRules = 'minlength: 8; required: lower; required: upper; required: digit;';

function Content() {
  const { replace } = useRouter();

  const { appendRedirectPath } = useRedirectPath();

  const { email, verification: verificationCode } = useLocalSearchParams<{
    email?: string;
    verification?: string;
  }>();

  const { resetPassword } = useAuth();

  const toast = useToaster();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    resolver: zodResolver(formSchema),
  });

  const [showPassword, setShowPassword] = useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const onSubmit = handleSubmit(async ({ password }) => {
    const invalidParams =
      !email || !verificationCode || !paramsSchema.safeParse({ email, verificationCode }).success;

    try {
      if (invalidParams) {
        toast.showError({
          title: "We're unable to create a new password. Please request a new reset password.",
        });

        replace(appendRedirectPath(ROUTES.FORGOT_PASSWORD));

        return;
      }

      await resetPassword({
        email,
        verificationCode,
        newPassword: password,
      });

      trackPasswordChanged(email, 'create-password');

      replace(appendRedirectPath('/create-password/success'));
    } catch (error) {
      if (error instanceof Error) {
        trackPasswordChangeFailed(error, email, 'create-password');
      }

      console.error('CreatePassword ERROR:', error);

      if (error instanceof Error) {
        if (
          [COGNITO_EXCEPTIONS.EXPIRED_CODE, COGNITO_EXCEPTIONS.CODE_MISMATCH].includes(
            error?.name
          ) &&
          !invalidParams
        ) {
          replace(`/create-password/verification-code-expired?email=${encodeURIComponent(email)}`);

          return;
        }

        if (error.name === COGNITO_EXCEPTIONS.LIMIT_EXCEEDED) {
          toast.showError({
            title:
              'You have exceeded the number of attempts to reset your password. Please request a new reset password.',
          });

          replace(appendRedirectPath(ROUTES.FORGOT_PASSWORD));

          return;
        }
      }

      toast.showError({
        title: 'There was an error resetting your password. Please try again.',
      });
    }
  });

  const { colors } = useColors();

  const { bottom } = useSafeAreaInsets();

  return (
    <VStack
      className="flex-1"
      style={{
        paddingBottom: bottom,
      }}>
      <Header
        contentScrollsUnderHeader
        headerShown={!isWeb}
        title="Set your password"
        statusBarBackgroundColor={colors.background[0]}
      />

      <KeyboardAwareScrollView
        contentInsetAdjustmentBehavior="automatic"
        mode="layout"
        bottomOffset={12}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        contentContainerClassName="flex-col flex-grow py-6 px-5 gap-y-8">
        <VStack className="flex-1">
          {isWeb ? (
            <Heading size="h1-xl" className="mb-2">
              Set your password
            </Heading>
          ) : null}

          <Text>Password requirements:</Text>

          <Text> • Must be a minimum of 8 characters</Text>

          <Text> • Must include an uppercase letter</Text>

          <Text> • Must include a lowercase letter</Text>

          <Text className="mb-8"> • Must include a number</Text>

          <VStack space="4xl">
            <Controller
              name="password"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl isInvalid={!!errors.password?.message}>
                  <FormControlLabel className="mb-2">
                    <FormControlLabelText>Password</FormControlLabelText>
                  </FormControlLabel>

                  <Input>
                    <InputField
                      autoCorrect={false}
                      autoCapitalize="none"
                      style={{ color: colors.typography[900] }}
                      value={value}
                      onChangeText={onChange}
                      type={showPassword ? 'text' : 'password'}
                      passwordRules={passwordRules}
                    />

                    <InputSlot className="pr-3" onPress={() => setShowPassword((prev) => !prev)}>
                      <InputIcon as={showPassword ? EyeIcon : EyeOffIcon} />
                    </InputSlot>
                  </Input>

                  <FormControlError className="mt-2">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />

                    <FormControlErrorText size="xs">
                      {errors.password?.message}
                    </FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl isInvalid={!!errors.confirmPassword?.message}>
                  <FormControlLabel className="mb-2">
                    <FormControlLabelText>Re-enter password</FormControlLabelText>
                  </FormControlLabel>

                  <Input>
                    <InputField
                      autoCorrect={false}
                      autoCapitalize="none"
                      style={{ color: colors.typography[900] }}
                      value={value}
                      onChangeText={onChange}
                      type={showConfirmPassword ? 'text' : 'password'}
                      passwordRules={passwordRules}
                    />

                    <InputSlot
                      className="pr-3"
                      onPress={() => setShowConfirmPassword((prev) => !prev)}>
                      <InputIcon as={showConfirmPassword ? EyeIcon : EyeOffIcon} />
                    </InputSlot>
                  </Input>

                  <FormControlError className="mt-2">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />

                    <FormControlErrorText size="xs">
                      {errors.confirmPassword?.message}
                    </FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />
          </VStack>
        </VStack>

        <VStack space="lg">
          <Button className="gap-x-2" onPress={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? <ButtonSpinner color={colors.primary[200]} /> : null}

            <ButtonText>Submit</ButtonText>
          </Button>

          <HStack className="flex-wrap justify-center">
            <Text>By proceeding, you agree to </Text>

            <Link isExternal href={TERMS_OF_USE_URL}>
              <LinkText className="text-primary-500 data-[hover=true]:text-primary-600">
                Terms of Use
              </LinkText>
            </Link>

            <Text> and </Text>

            <Link isExternal href={PRIVACY_POLICY_URL}>
              <LinkText className="text-primary-500 data-[hover=true]:text-primary-600">
                Privacy Policy
              </LinkText>
            </Link>
          </HStack>
        </VStack>
      </KeyboardAwareScrollView>
    </VStack>
  );
}

export default function CreatePassword() {
  if (isWeb) {
    return (
      <UnauthenticatedLayout>
        <Content />
      </UnauthenticatedLayout>
    );
  }

  return <Content />;
}
